/**
 * Filter state that lives in the URL query string.
 *
 * The URL is the sharing mechanism: a staff member investigating something pastes the link into a
 * channel and everyone else lands on the same view. That only works if every filter round-trips
 * through the address bar, so this is the single source of truth rather than a mirror of component
 * state — there is no second copy to drift.
 *
 * History entries are REPLACED rather than pushed for filter edits. Typing eight characters into the
 * search box should not put eight entries in the back stack; back should leave the page.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { LogType } from "../types";

export interface Filters {
  player: string;
  types: LogType[];
  server: string;
  from: string;
  to: string;
  q: string;
  /** Which page is open. Part of the URL so a link can point straight at the voice tab. */
  tab: "home" | "overview" | "players" | "logs" | "dupe" | "stashes" | "wagerdebug" | "voice" | "discord" | "portal" | "admin" | "settings" | "security" | "profile" | "maps";
  /** Player profile being viewed: a uuid, or a name until it is resolved. Lives in the path. */
  profile: string;
  /** Login alert to show on the security page, from the link in a notification. */
  alert: string;
  /** Player looked up in the staff portal. */
  lookup: string;
  /** Open staff-portal section; empty shows the portal home screen. */
  section: "" | "loa" | "punishments" | "missing";
  /** Player whose drawer is open, if any. */
  drawer: string;
  /** A single voice event to focus, from the link a Discord alert carries. */
  event: string;
  live: boolean;
}

const EMPTY: Filters = {
  player: "",
  types: [],
  server: "",
  from: "",
  to: "",
  q: "",
  tab: "home",
  profile: "",
  alert: "",
  lookup: "",
  section: "",
  drawer: "",
  event: "",
  live: true,
};

/** Pages with their own path: security (notifications link to it) and player profiles. */
const SECURITY_PATH = "/account/security";
const PROFILE_PATH = /^\/players(?:\/([^/?#]+))?\/?$/;
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parse(search: string, pathname = window.location.pathname): Filters {
  const params = new URLSearchParams(search);
  const profileMatch = PROFILE_PATH.exec(pathname);
  const tab = params.get("tab")
    ? params.get("tab")
    : pathname.replace(/\/+$/, "") === SECURITY_PATH
      ? "security"
      : profileMatch
        ? "profile"
        : null;
  const tabs = ["home", "overview", "players", "logs", "dupe", "stashes", "wagerdebug", "voice", "discord", "portal", "admin", "settings", "security", "profile", "maps"] as const;
  return {
    player: params.get("player") ?? "",
    types: params.getAll("type").flatMap((v) => v.split(",")).filter(Boolean) as LogType[],
    server: params.get("server") ?? "",
    from: params.get("from") ?? "",
    to: params.get("to") ?? "",
    q: params.get("q") ?? "",
    // The home screen is the landing page, so an unrecognised or absent tab lands there.
    tab: (tabs as readonly string[]).includes(tab ?? "") ? (tab as Filters["tab"]) : "home",
    profile: profileMatch?.[1] ? decodeURIComponent(profileMatch[1]).slice(0, 36) : "",
    alert: /^\d+$/.test(params.get("alert") ?? "") ? (params.get("alert") as string) : "",
    lookup: params.get("lookup") ?? "",
    section: (["loa", "punishments", "missing"] as const).find((s) => s === params.get("section")) ?? "",
    drawer: params.get("drawer") ?? "",
    // Digits only: this goes straight into an API query, and a link is the one filter that arrives
    // from outside the app — pasted out of Discord by whoever was reading the alert.
    event: /^\d+$/.test(params.get("event") ?? "") ? (params.get("event") as string) : "",
    // Live tail is ON unless the URL explicitly turns it off, so a shared link behaves the way the
    // dashboard does by default.
    live: params.get("live") !== "0",
  };
}

function serialise(filters: Filters): string {
  const params = new URLSearchParams();
  if (filters.player) params.set("player", filters.player);
  for (const type of filters.types) params.append("type", type);
  if (filters.server) params.set("server", filters.server);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.q) params.set("q", filters.q);
  if (filters.tab !== "home" && filters.tab !== "security" && filters.tab !== "profile") params.set("tab", filters.tab);
  if (filters.tab === "security" && filters.alert) params.set("alert", filters.alert);
  if (filters.tab === "portal" && filters.section) params.set("section", filters.section);
  if (filters.lookup) params.set("lookup", filters.lookup);
  if (filters.drawer) params.set("drawer", filters.drawer);
  if (filters.event) params.set("event", filters.event);
  if (!filters.live) params.set("live", "0");
  return params.toString();
}

/** Security and profiles live at their own paths; everything else at the site root with ?tab=. */
function urlFor(filters: Filters): string {
  const query = serialise(filters);
  const path =
    filters.tab === "security"
      ? SECURITY_PATH
      : filters.tab === "profile"
        ? `/players${filters.profile ? `/${encodeURIComponent(filters.profile)}` : ""}`
        : "/";
  return query ? `${path}?${query}` : path;
}

export function useUrlFilters(): {
  filters: Filters;
  setFilters: (next: Partial<Filters>) => void;
  reset: () => void;
  /** Only the parts the log endpoints care about — so opening a drawer or switching tabs does not
   *  invalidate the log query or reconnect the stream. */
  logQuery: string;
} {
  const [filters, setState] = useState<Filters>(() => parse(window.location.search));

  useEffect(() => {
    const onPop = (): void => setState(parse(window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const setFilters = useCallback((next: Partial<Filters>) => {
    setState((current) => {
      const merged = { ...current, ...next };
      // Opening a portal section is a real step forward, so it gets a history entry and the
      // browser's own Back returns to the portal home. Everything else replaces, as before.
      const opensSection =
        (Boolean(merged.section) && merged.section !== current.section) ||
        (merged.tab === "profile" &&
          Boolean(merged.profile) &&
          merged.profile !== current.profile &&
          // Turning /players/<name> into /players/<uuid> is the same page: replace, so Back does not
          // land on the name URL and get bounced forward again.
          !(current.tab === "profile" && current.profile && !UUID_SHAPE.test(current.profile)));
      if (opensSection) window.history.pushState(null, "", urlFor(merged));
      else window.history.replaceState(null, "", urlFor(merged));
      return merged;
    });
  }, []);

  // Clears the FILTERS, not the page. Resetting `tab` here would make "Clear all" on the log list
  // throw you back to the overview, which is not what the button says it does.
  const reset = useCallback(() => {
    setState((current) => {
      const next = { ...EMPTY, tab: current.tab, live: current.live };
      window.history.replaceState(null, "", urlFor(next));
      return next;
    });
  }, []);

  const logQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.player) params.set("player", filters.player);
    for (const type of filters.types) params.append("type", type);
    if (filters.server) params.set("server", filters.server);
    if (filters.from) params.set("from", String(Date.parse(filters.from)));
    // A date input gives midnight; the user means the end of that day.
    if (filters.to) params.set("to", String(Date.parse(filters.to) + 86_399_999));
    if (filters.q) params.set("q", filters.q);
    return params.toString();
  }, [filters.player, filters.types, filters.server, filters.from, filters.to, filters.q]);

  return { filters, setFilters, reset, logQuery };
}

/** Debounce for the free-text box. 250ms, per the brief: long enough to skip most intermediate
 *  keystrokes, short enough that it does not feel laggy. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}
